/**
 * OCM Complexity Diagnostic — item definitions
 * Shared between marketing (/diagnostic) and practitioner (/diagnostic/:engagementId) pages.
 */

export const TRIGGER_ITEMS: string[] = [
  'A new or replacement IT system will be implemented.',
  'Existing business processes or workflows will change.',
  'Employee roles or responsibilities will change.',
  'Job classifications, reporting lines, or job architecture will be affected.',
  'Policies, regulations, or compliance requirements are changing.',
  'Service delivery to the public will change.',
  'Data entry, data quality, or reporting expectations will change.',
  'Multiple departments or agencies are involved.',
]

export interface DropdownOption {
  value: number
  label: string
}

export interface DropdownItem {
  key: string
  label: string
  section?: 'context' | 'amplifiers'
  options: DropdownOption[]
  hint?: string
}

// Marketing version: 3 dropdowns (max 9 pts dropdown + 8 triggers = 17 total)
export const MARKETING_DROPDOWNS: DropdownItem[] = [
  {
    key: 'people_impact',
    label: 'People Impact — how significantly will this change how employees do day-to-day work?',
    options: [
      { value: 1, label: 'Low — minor adjustments to current routines (1)' },
      { value: 2, label: 'Moderate — employees will need to learn new ways of working (2)' },
      { value: 3, label: 'High — significant change to how most employees do their jobs (3)' },
    ],
  },
  {
    key: 'process_impact',
    label: 'Process Impact — how significantly will processes or systems change?',
    options: [
      { value: 1, label: 'Low — existing processes mostly intact (1)' },
      { value: 2, label: 'Moderate — several processes will be redesigned (2)' },
      { value: 3, label: 'High — major systems or end-to-end processes are being replaced (3)' },
    ],
  },
  {
    key: 'scope',
    label: 'Scope — who is primarily affected by this change?',
    options: [
      { value: 1, label: 'A team, unit, or small group (1)' },
      { value: 2, label: 'A program, division, or department (2)' },
      { value: 3, label: 'The whole agency or multiple departments (3)' },
    ],
  },
]

// Practitioner version: 12 dropdowns (max 34 pts dropdown + 8 triggers = 42 total)
export const PRACTITIONER_DROPDOWNS: DropdownItem[] = [
  // ── Core context
  {
    key: 'people_impact',
    label: 'People Impact',
    section: 'context',
    options: [
      { value: 1, label: 'Low (1)' },
      { value: 2, label: 'Moderate (2)' },
      { value: 3, label: 'High (3)' },
    ],
  },
  {
    key: 'process_impact',
    label: 'Process Impact',
    section: 'context',
    options: [
      { value: 1, label: 'Low (1)' },
      { value: 2, label: 'Moderate (2)' },
      { value: 3, label: 'High (3)' },
    ],
  },
  {
    key: 'scope',
    label: 'Scope',
    section: 'context',
    options: [
      { value: 1, label: 'Team / Unit (1)' },
      { value: 2, label: 'Program / Division (2)' },
      { value: 3, label: 'Enterprise / Multi-division (3)' },
    ],
  },
  {
    key: 'visibility',
    label: 'Visibility',
    section: 'context',
    options: [
      { value: 0, label: 'Internal only (0)' },
      { value: 1, label: 'External partners / customers (1)' },
      { value: 2, label: 'High public / legislative (2)' },
    ],
  },
  {
    key: 'mandate',
    label: 'Mandate Pressure — is this change externally mandated or compliance-driven?',
    section: 'context',
    options: [
      { value: 0, label: 'No (0)' },
      { value: 1, label: 'Not sure (1)' },
      { value: 2, label: 'Yes (2)' },
    ],
  },
  {
    key: 'saturation',
    label: 'Change Saturation — is the organization already undergoing other significant changes?',
    section: 'context',
    options: [
      { value: 0, label: 'No (0)' },
      { value: 1, label: 'Not sure (1)' },
      { value: 2, label: 'Yes (2)' },
    ],
  },
  {
    key: 'stakeholders',
    label: 'Stakeholder Complexity',
    section: 'context',
    options: [
      { value: 1, label: 'Low (1)' },
      { value: 2, label: 'Moderate (2)' },
      { value: 3, label: 'High (3)' },
    ],
  },
  {
    key: 'consequence',
    label: 'Consequence — what happens if adoption is inconsistent?',
    section: 'context',
    options: [
      { value: 0, label: 'Minor inconvenience (0)' },
      { value: 1, label: 'Operational rework (1)' },
      { value: 2, label: 'Service / compliance / reputation impact (2)' },
    ],
  },
  // ── V2 risk amplifiers
  {
    key: 'curiosity',
    label: 'Curiosity / Discovery — how much discovery work has been done before this initiative launched?',
    section: 'amplifiers',
    hint: 'Skipping discovery is a key OCM risk amplifier — it scores highest because assumptions compound.',
    options: [
      { value: 1, label: 'Substantial discovery completed (1)' },
      { value: 3, label: 'Limited or no discovery done (3)' },
      { value: 5, label: 'Discovery skipped entirely (5)' },
    ],
  },
  {
    key: 'organic',
    label: 'Organic vs. Mechanistic Fit — how well does this change fit the nature of the work?',
    section: 'amplifiers',
    options: [
      { value: 1, label: 'Good fit — work is routine and standardization makes sense (1)' },
      { value: 2, label: 'Moderate tension — some discretion needed (2)' },
      { value: 3, label: 'High tension — work requires flexibility the change may not support (3)' },
    ],
  },
  {
    key: 'procurement',
    label: 'Procurement Risk Ownership — how is OCM addressed in the vendor / solicitation arrangement?',
    section: 'amplifiers',
    options: [
      { value: 1, label: 'OCM is explicitly scoped and owned internally (1)' },
      { value: 2, label: 'OCM is delegated to vendor with limited agency oversight (2)' },
      { value: 3, label: 'OCM is not addressed in the solicitation (3)' },
    ],
  },
  {
    key: 'lq',
    label: 'Behavioral Readiness — what is the workforce\'s change history and capacity?',
    section: 'amplifiers',
    options: [
      { value: 1, label: 'High — strong change track record and appetite (1)' },
      { value: 2, label: 'Moderate — mixed history; some resistance expected (2)' },
      { value: 3, label: 'Low — high resistance, change fatigue, or limited capacity (3)' },
    ],
  },
]

// Tier metadata (matches Python TIER_META exactly)
export type TierKey = 'Foundational' | 'Targeted' | 'Comprehensive' | 'Enterprise'

export interface TierMeta {
  label: string
  short: string
  interpretation: string
  recommendations: string[]
  colorClass: 'tierFoundational' | 'tierTargeted' | 'tierComprehensive' | 'tierEnterprise'
}

export const TIER_META: Record<TierKey, TierMeta> = {
  Foundational: {
    label: 'Foundational OCM',
    short: 'Low complexity. Standard project communications and basic manager awareness are likely sufficient.',
    interpretation: 'Limited scope, low impact, or few concurrent change pressures. Standard project communications and basic training are likely sufficient. Assign a manager-level champion rather than a dedicated OCM resource.',
    recommendations: [
      'Develop a concise communication plan: what is changing, why, what employees need to do, who to contact.',
      'Brief the immediate manager layer — they are the primary change channel at this tier.',
      'Set a 30-day adoption check-in with the sponsor. Document the change in SOPs.',
    ],
    colorClass: 'tierFoundational',
  },
  Targeted: {
    label: 'Targeted OCM',
    short: 'Moderate complexity. A dedicated OCM resource and role-based communications are warranted.',
    interpretation: 'Specific roles or functions are meaningfully affected. General communications will miss significant stakeholder segments. A dedicated OCM resource and role-based communication tracks are warranted.',
    recommendations: [
      'Conduct a targeted Change Impact Assessment for affected roles before designing training.',
      'Build role-specific communication tracks: general employee + manager/supervisor versions at minimum.',
      'Establish a sponsor coalition. Plan for a formal adoption check (CODE-C) at 30–60 days post go-live.',
    ],
    colorClass: 'tierTargeted',
  },
  Comprehensive: {
    label: 'Comprehensive OCM',
    short: 'High complexity. Active resistance management, adoption tracking, and leadership alignment are required.',
    interpretation: 'High-impact, mandated, or multi-stakeholder initiative where adoption is not optional. Resistance management, adoption tracking, and leadership alignment are core OCM deliverables — not optional add-ons.',
    recommendations: [
      'Establish OCM governance with executive sponsorship and a formal change workstream.',
      'Conduct Change Impact Assessment and Empathy Mapping before communications design.',
      'Administer CODE-C at baseline, pre-go-live, and 60-day post-launch.',
      'Design reinforcement extending at least 90 days post go-live. Build a resistance escalation path.',
    ],
    colorClass: 'tierComprehensive',
  },
  Enterprise: {
    label: 'High-Risk / Enterprise OCM',
    short: 'Very high complexity. Governance-integrated OCM required.',
    interpretation: 'Enterprise-wide or highly visible initiative where mission continuity, public trust, or compliance are at stake. OCM failure has organizational and reputational consequences beyond the program itself. Governance-integrated OCM is required.',
    recommendations: [
      'Require executive sponsor commitment as a precondition of program launch. Define sustained visibility in behavioral terms.',
      'Establish a formal Change Advisory Board with HR/labor relations representation.',
      'Design for 12-month post-launch sustainment. Include adoption measurement, reinforcement routines, and governance hand-off plan.',
      'If Curiosity/Discovery scored 5 pts: stop. Complete discovery before any further program design.',
    ],
    colorClass: 'tierEnterprise',
  },
}

// Scoring thresholds (client-side, for live preview)
export function scoreMarketing(triggerCount: number, inputs: Record<string, number>): { total: number; tier: TierKey } {
  const dropdownTotal = (inputs.people_impact ?? 0) + (inputs.process_impact ?? 0) + (inputs.scope ?? 0)
  const total = triggerCount + dropdownTotal
  let tier: TierKey = 'Foundational'
  if (total >= 14) tier = 'Enterprise'
  else if (total >= 10) tier = 'Comprehensive'
  else if (total >= 6)  tier = 'Targeted'
  return { total, tier }
}

export function scorePractitioner(triggerCount: number, inputs: Record<string, number>): { total: number; tier: TierKey } {
  const dropdownTotal = Object.values(inputs).reduce((s, v) => s + (v ?? 0), 0)
  const total = triggerCount + dropdownTotal
  let tier: TierKey = 'Foundational'
  if (total >= 36) tier = 'Enterprise'
  else if (total >= 26) tier = 'Comprehensive'
  else if (total >= 16) tier = 'Targeted'
  return { total, tier }
}
